<template>
    <div class="login">
        <div class="login__bg login__bg--reset"></div>
        <div class="container container--medium">

            <div class="login__content">

                <div class="login__content__img">
                    <img alt="PUMATRAC" src="../assets/img/reset_header.jpg" />
                </div>

                <div class="login__header">
                    <div class="login__wrapper">
                        <nav class="login__nav">
                            <span class="login__nav__link is-active"><translate>reset_password</translate></span>
                        </nav>
                    </div>
                </div>

                <div class="login__form">
                    <div class="login__wrapper">
                        <validation-observer v-slot="{ handleSubmit }">
                            <form class="form" @submit.prevent="handleSubmit(formSubmitHandler)" v-if="!regularSuccess">

                                <validation-provider 
                                    class="form__row" 
                                    rules="required|min:6" 
                                    vid="password" 
                                    :name="passwordPlaceholder"
                                    v-slot="{ errors }"
                                >
                                    <div class="form__item">
                                        <input 
                                            v-model="password" 
                                            @input="passwordChangeHandler" 
                                            :type="passwordFieldType" 
                                            class="login__form__field form__field form__field--password" 
                                            :placeholder="passwordPlaceholder" 
                                            name="password" 
                                            autocomplete="current-password"
                                            :class="{'is-error': errors.length}"
                                        />
                                        <div class="form__show-password" :class="[{'is-password-visible': passwordFieldPasswordVisible}, {'is-switch-visible': passwordFieldFilled}]" @click="() => togglePassword('original')">
                                            <span class="form__show-password__show" v-translate>show</span>
                                            <span class="form__show-password__hide" v-translate>hide</span>
                                        </div>
                                    </div>
                                    <div class="form__error">{{ errors[0] }}</div>
                                </validation-provider>

                                <validation-provider class="form__row" rules="required|confirmed:password" v-slot="{ errors }">
                                    <div class="form__item">
                                        <input 
                                            v-model="passwordRepeat" 
                                            @input="passwordRepeatChangeHandler" 
                                            :type="passwordRepeatFieldType"
                                            class="login__form__field form__field form__field--password" 
                                            :placeholder="passwordRepeatPlaceholder" 
                                            name="passwordRepeat" 
                                            :class="{'is-error': errors.length}"
                                        />
                                        <div class="form__show-password" :class="[{'is-password-visible': passwordRepeatFieldPasswordVisible}, {'is-switch-visible': passwordRepeatFieldFilled}]" @click="() => togglePassword('repeat')">
                                            <span class="form__show-password__show" v-translate>show</span>
                                            <span class="form__show-password__hide" v-translate>hide</span>
                                        </div>
                                    </div>
                                    <div class="form__error">{{ errors[0] }}</div>
                                </validation-provider>
        
                                <puma-button class="login__form__submit login__button button--border" type="submit" :loading="submitButtonLoading">
                                    <span v-translate>reset_password</span>
                                </puma-button>
                                <div class="form__error" v-if="regularError">{{ regularError }}</div>
                            </form>
                        </validation-observer>

                        <div class="form__error form__error--success" v-if="regularSuccess">{{ regularSuccess }}</div>

                    </div>
                </div>

                <div class="login__back">
                    <router-link :to="{ name: 'Discover' }"><translate>back_to_pumatrac</translate></router-link>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import * as api from '../api'

import {addTrackingObject} from "@/utils/tracking"

export default {
    name: "ForgotPassword",
    props: {
        mode: {
            type: String
        }
    },
    data () {
        return {
            token: '',

            submitButtonLoading: false,
            regularError: false,
            regularSuccess: false,

            password: '',
            passwordFieldType: 'text',
            passwordFieldPasswordVisible: false,
            passwordFieldFilled: false,
            passwordOriginalDirty: false,
            passwordRepeat: '',
            passwordRepeatFieldType: 'text',
            passwordRepeatFieldPasswordVisible: false,
            passwordRepeatFieldFilled: false,
            passwordRepeatDirty: false,
            
        }
    },
    computed: {
        passwordPlaceholder() {
            return this.$gettext('password');
        },
        passwordRepeatPlaceholder() {
            return this.$gettext('password_repeat');
        }
    },
    mounted() {
        if(typeof this.$router.history.current.query.token === 'undefined'){
            this.$router.push('/login');
        } else {
            this.token = this.$router.history.current.query.token;
            addTrackingObject({subSection: "login", screenName: "resetPassword"}, {type: "login", value: "web_reset_password"});
        }
    },
    methods: {
        formSubmitHandler() {
            this.resetPassword();
        },
        togglePassword(type) {
            if(type === 'original') {
                this.passwordFieldType = this.passwordFieldPasswordVisible ? 'password' : 'text';    
                this.passwordFieldPasswordVisible = !this.passwordFieldPasswordVisible;
                this.passwordOriginalDirty = true;
            } else if(type === 'repeat') {
                this.passwordRepeatFieldType = this.passwordRepeatFieldPasswordVisible ? 'password' : 'text';    
                this.passwordRepeatFieldPasswordVisible = !this.passwordRepeatFieldPasswordVisible;
                this.passwordRepeatDirty = true;
            }
        },
        passwordChangeHandler() {
            this.passwordPlaceholderToggle('original')
        },
        passwordRepeatChangeHandler() {
            this.passwordPlaceholderToggle('repeat')
        },
        passwordPlaceholderToggle(type) {
            let inputType = 'password';
            let fieldValue = type === 'original' ? this.password : this.passwordRepeat;
            if(!fieldValue.length) {
                inputType = 'text'
                if(this.passwordFieldPasswordVisible){
                    this.togglePassword(type);
                }
            }
            if(type === 'original') {
                this.passwordFieldFilled = !!fieldValue.length;
                if(!this.passwordOriginalDirty) {
                    this.passwordFieldType = inputType
                }
            } else {
                this.passwordRepeatFieldFilled = !!fieldValue.length;
                if(!this.passwordRepeatDirty) {
                    this.passwordRepeatFieldType = inputType
                }
            }
        },
        async resetPassword() {
            this.submitButtonLoading = true;
            const data = {
                token: this.token,
                password: this.password
            }
            try {
                const resetResponse = await api.users.updatePassword(data);

                if(resetResponse.status == 200 || resetResponse.status == 204){
                    this.regularSuccess = this.$gettext('password_reset_successful');
                }

            } catch (error) {
                if(error.response.status === 400) {
                    this.regularError = this.$gettext('form_error_no_valid_request');
                // } else if(error.response.status === 404) {
                //     this.regularError = this.$gettext('password_reset_error_no_email');
                // } else if(error.response.status === 400) {
                //     this.regularError = this.$gettext('password_reset_error_social');
                }
            } finally {
                this.submitButtonLoading = false;
            }
        }
    }
}
</script>

<style scoped lang="scss">
.login {
    $block: &;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;

    &__bg {
        @include fillout;
        background-size: cover;
        background-position: center;
        background-image: url('../assets/img/bg-download.jpg');

        position: fixed;
        left: 0;
        top: 0;
        width: 100vw;
        height: 100vh;

        &--reset {
            background-image: url('../assets/img/bg-password.jpg');
        }
    }

    &__content {
        background-color: white;
        position: relative;
        z-index: 1;

        &__img {
            width: 100%;
            height: 170px;

            @include mq(tablet) {
                height: 280px;
            }

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                object-position: center bottom;
            }
        }
    }

    &__wrapper {
        width: 100%;
        max-width: 410px;
        margin: 0 auto;
        padding: 0 20px;
    }

    &__header {
        border-bottom: 1px solid rgba(#000, 0.1);
    }

    &__nav {
        display: flex;
        &__link {
            flex-shrink: 0;
            flex-grow: 1;
            font-weight: $font-weight-bold;
            color: $puma-color-black;
            text-align: center;
            position: relative;
            padding: 2.5em 1em 0.75em;
            text-transform: uppercase;
            cursor: pointer;
            &:hover {
                &::after {
                    transform: none;
                }
            }
            &.is-active {
                pointer-events: none;
                &::after {
                    transform: none;
                    background-color: $puma-color-black;
                }
            }
            &::after {
                content: '';
                transition:  0.2s;
                position: absolute;
                bottom: -1px;
                left: 0;
                width: 100%;
                height: 2px;
                background-color: $puma-color-neutral-20;
                transform: scaleY(0);
                transform-origin: bottom center;
            }
        }
    }

    &__social {
        padding: 60px 0 46px;
    }

    &__button {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 8px;
        &:last-child {
            margin-bottom: 0;
        }
        &__icon {
            margin-right: 12px;
        }
    }
    &__separator {
        position: relative;
        display: flex;
        justify-content: center;
        &::after {
            content: '';
            position: absolute;
            top: 50%;
            left: 0;
            width: 100%;
            height: 1px;
            background-color: rgba(#000, 0.1);
        }
        &__text {
            background-color: white;
            padding: 0 2em;
            font-size: $puma-size-font-lg;
            position: relative;
            z-index: 1;
        }
    }
    &__form {
        padding: 40px 0 64px;
        &__submit {
            margin-top: 24px;
            font-size: $puma-size-font-md;
        }
    }
    &__password-link {
        text-decoration: underline;
    }

    &__back {
        width: 100%;
        text-align: center;
        padding-bottom: 20px;

        a {
            position: relative;
            font-size: $puma-size-font-xs;
            display: inline-block;
            padding-bottom: 5px;

            &:after {
                content: '';
                position: absolute;
                bottom: 3px;
                left: 50%;
                transform: translateX(-50%);
                width: 100%;
                height: 1px;
                background: black;
                transition: all .15s ease;
            }

            &:hover {
                &:after {
                    width: 10%;
                }
            }
        }
    }
}
.form {
    &__error {
        &--success {
            text-align: center;
            color: $puma-color-black;
            font-size: $puma-size-font-md;
            margin-top: 40px;
        }
    }
}
</style>